import { Vec3 } from './Vec3.js';

let _nextColliderId = 1;

/**
 * Base Collider class.
 */
export class Collider {
  constructor(options = {}) {
    this.id = options.id || _nextColliderId++;
    this.name = options.name || `collider_${this.id}`;
    this.type = options.type || 'collider';
    this.isTrigger = !!options.isTrigger;
    this.isWalkable = options.isWalkable !== undefined ? options.isWalkable : true;
    this.friction = options.friction !== undefined ? options.friction : 0.6;
    this.restitution = options.restitution !== undefined ? options.restitution : 0.0;
    this.bounds = {
      min: new Vec3(-Infinity, -Infinity, -Infinity),
      max: new Vec3(Infinity, Infinity, Infinity)
    };
  }

  updateBounds() {
    // Override in derived classes
  }

  containsPoint(point) {
    return false;
  }

  closestPoint(point, out = new Vec3()) {
    return out.copy(point);
  }

  raycast(rayOrigin, rayDir, maxDist = Infinity) {
    return null;
  }

  testSphere(center, radius) {
    return null;
  }

  testCapsule(baseCenter, topCenter, radius) {
    return null;
  }
}

/**
 * PlaneCollider (Ground or infinite / bounded planar surface).
 */
export class PlaneCollider extends Collider {
  constructor(options = {}) {
    super({ ...options, type: 'plane' });
    this.point = Vec3.from(options.point || { x: 0, y: 0, z: 0 });
    this.normal = Vec3.from(options.normal || { x: 0, y: 1, z: 0 }).normalize();
    this.size = options.size || null; // null for infinite, or [minX, maxX, minZ, maxZ]

    this.updateBounds();
  }

  updateBounds() {
    if (!this.size) {
      this.bounds.min.set(-1e6, this.point.y - 0.1, -1e6);
      this.bounds.max.set(1e6, this.point.y + 0.1, 1e6);
    } else {
      this.bounds.min.set(this.size[0], this.point.y - 0.5, this.size[2]);
      this.bounds.max.set(this.size[1], this.point.y + 0.5, this.size[3]);
    }
  }

  raycast(rayOrigin, rayDir, maxDist = Infinity) {
    const denom = this.normal.dot(rayDir);
    if (Math.abs(denom) < 1e-6) return null;

    const diff = new Vec3().subVectors(this.point, rayOrigin);
    const t = diff.dot(this.normal) / denom;
    if (t < 0 || t > maxDist) return null;

    const hitPoint = new Vec3().addVectors(rayOrigin, new Vec3().copy(rayDir).multiplyScalar(t));

    if (this.size) {
      if (
        hitPoint.x < this.size[0] || hitPoint.x > this.size[1] ||
        hitPoint.z < this.size[2] || hitPoint.z > this.size[3]
      ) {
        return null;
      }
    }

    return {
      hit: true,
      distance: t,
      point: hitPoint,
      normal: denom < 0 ? this.normal.clone() : this.normal.clone().negate(),
      collider: this
    };
  }

  testSphere(center, radius) {
    if (this.size) {
      if (
        center.x < this.size[0] - radius || center.x > this.size[1] + radius ||
        center.z < this.size[2] - radius || center.z > this.size[3] + radius
      ) {
        return null;
      }
    }

    const dist = (center.x - this.point.x) * this.normal.x +
                 (center.y - this.point.y) * this.normal.y +
                 (center.z - this.point.z) * this.normal.z;

    if (dist <= radius && dist > -radius * 2) {
      const penetration = radius - dist;
      const contactPoint = new Vec3().copy(center).addScaledVector(this.normal, -dist);
      return {
        hit: true,
        penetration,
        normal: this.normal.clone(),
        point: contactPoint,
        collider: this
      };
    }
    return null;
  }

  testCapsule(baseCenter, topCenter, radius) {
    const d0 = (baseCenter.x - this.point.x) * this.normal.x +
               (baseCenter.y - this.point.y) * this.normal.y +
               (baseCenter.z - this.point.z) * this.normal.z;
    const d1 = (topCenter.x - this.point.x) * this.normal.x +
               (topCenter.y - this.point.y) * this.normal.y +
               (topCenter.z - this.point.z) * this.normal.z;

    const closestSphereCenter = d0 <= d1 ? baseCenter : topCenter;
    return this.testSphere(closestSphereCenter, radius);
  }
}

/**
 * BoxCollider (Supports Axis-Aligned AABB and Oriented OBB).
 */
export class BoxCollider extends Collider {
  constructor(options = {}) {
    super({ ...options, type: 'box' });

    if (options.min && options.max) {
      const min = Vec3.from(options.min);
      const max = Vec3.from(options.max);
      this.center = new Vec3(
        (min.x + max.x) * 0.5,
        (min.y + max.y) * 0.5,
        (min.z + max.z) * 0.5
      );
      this.size = new Vec3(
        max.x - min.x,
        max.y - min.y,
        max.z - min.z
      );
      this.halfExtents = new Vec3(this.size.x * 0.5, this.size.y * 0.5, this.size.z * 0.5);
      this.rotationY = options.rotationY || 0;
    } else {
      this.center = Vec3.from(options.center || options.position || { x: 0, y: 0, z: 0 });
      this.size = Vec3.from(options.size || { x: 1, y: 1, z: 1 });
      this.halfExtents = new Vec3(this.size.x * 0.5, this.size.y * 0.5, this.size.z * 0.5);
      this.rotationY = options.rotationY || (typeof options.rotation === 'number' ? options.rotation : 0);
    }

    this.isOBB = Math.abs(this.rotationY) > 1e-5;
    this.updateBounds();
  }

  updateBounds() {
    if (!this.isOBB) {
      this.bounds.min.set(
        this.center.x - this.halfExtents.x,
        this.center.y - this.halfExtents.y,
        this.center.z - this.halfExtents.z
      );
      this.bounds.max.set(
        this.center.x + this.halfExtents.x,
        this.center.y + this.halfExtents.y,
        this.center.z + this.halfExtents.z
      );
    } else {
      const cos = Math.cos(this.rotationY);
      const sin = Math.sin(this.rotationY);
      const hx = this.halfExtents.x;
      const hz = this.halfExtents.z;

      const ex = Math.abs(cos * hx) + Math.abs(sin * hz);
      const ez = Math.abs(-sin * hx) + Math.abs(cos * hz);

      this.bounds.min.set(
        this.center.x - ex,
        this.center.y - this.halfExtents.y,
        this.center.z - ez
      );
      this.bounds.max.set(
        this.center.x + ex,
        this.center.y + this.halfExtents.y,
        this.center.z + ez
      );
    }
  }

  worldToLocal(worldPoint, out = new Vec3()) {
    out.subVectors(worldPoint, this.center);
    if (this.isOBB) {
      const cos = Math.cos(-this.rotationY);
      const sin = Math.sin(-this.rotationY);
      const x = out.x * cos + out.z * sin;
      const z = -out.x * sin + out.z * cos;
      out.x = x;
      out.z = z;
    }
    return out;
  }

  localToWorld(localPoint, out = new Vec3()) {
    out.copy(localPoint);
    if (this.isOBB) {
      const cos = Math.cos(this.rotationY);
      const sin = Math.sin(this.rotationY);
      const x = out.x * cos + out.z * sin;
      const z = -out.x * sin + out.z * cos;
      out.x = x;
      out.z = z;
    }
    out.add(this.center);
    return out;
  }

  localToWorldVec(localVec, out = new Vec3()) {
    out.copy(localVec);
    if (this.isOBB) {
      const cos = Math.cos(this.rotationY);
      const sin = Math.sin(this.rotationY);
      const x = out.x * cos + out.z * sin;
      const z = -out.x * sin + out.z * cos;
      out.x = x;
      out.z = z;
    }
    return out;
  }

  worldToLocalVec(worldVec, out = new Vec3()) {
    out.copy(worldVec);
    if (this.isOBB) {
      const cos = Math.cos(-this.rotationY);
      const sin = Math.sin(-this.rotationY);
      const x = out.x * cos + out.z * sin;
      const z = -out.x * sin + out.z * cos;
      out.x = x;
      out.z = z;
    }
    return out;
  }

  containsPoint(point) {
    const local = this.worldToLocal(point);
    return (
      Math.abs(local.x) <= this.halfExtents.x &&
      Math.abs(local.y) <= this.halfExtents.y &&
      Math.abs(local.z) <= this.halfExtents.z
    );
  }

  closestPoint(point, out = new Vec3()) {
    const local = this.worldToLocal(point);
    local.x = Math.max(-this.halfExtents.x, Math.min(this.halfExtents.x, local.x));
    local.y = Math.max(-this.halfExtents.y, Math.min(this.halfExtents.y, local.y));
    local.z = Math.max(-this.halfExtents.z, Math.min(this.halfExtents.z, local.z));
    return this.localToWorld(local, out);
  }

  raycast(rayOrigin, rayDir, maxDist = Infinity) {
    const localOrigin = this.worldToLocal(rayOrigin);
    const localDir = this.worldToLocalVec(rayDir);

    let tmin = 0;
    let tmax = maxDist;
    let normalAxis = 0;
    let normalSign = 0;

    const axes = ['x', 'y', 'z'];
    for (let i = 0; i < 3; i++) {
      const axis = axes[i];
      const invD = 1.0 / (Math.abs(localDir[axis]) > 1e-9 ? localDir[axis] : 1e-9 * Math.sign(localDir[axis] || 1));
      let t0 = (-this.halfExtents[axis] - localOrigin[axis]) * invD;
      let t1 = (this.halfExtents[axis] - localOrigin[axis]) * invD;
      let sign = -1;

      if (invD < 0) {
        const tmp = t0;
        t0 = t1;
        t1 = tmp;
        sign = 1;
      }

      if (t0 > tmin) {
        tmin = t0;
        normalAxis = i + 1;
        normalSign = sign;
      }
      tmax = Math.min(tmax, t1);

      if (tmax < tmin) return null;
    }

    if (tmin < 0 || tmin > maxDist) return null;

    const localHit = new Vec3().addVectors(localOrigin, new Vec3().copy(localDir).multiplyScalar(tmin));
    const worldHit = this.localToWorld(localHit);

    const localNormal = new Vec3();
    if (normalAxis === 1) localNormal.x = normalSign;
    else if (normalAxis === 2) localNormal.y = normalSign;
    else if (normalAxis === 3) localNormal.z = normalSign;

    const worldNormal = this.localToWorldVec(localNormal).normalize();

    return {
      hit: true,
      distance: tmin,
      point: worldHit,
      normal: worldNormal,
      collider: this
    };
  }

  testSphere(center, radius) {
    const localCenter = this.worldToLocal(center);
    const hx = this.halfExtents.x;
    const hy = this.halfExtents.y;
    const hz = this.halfExtents.z;

    const cx = Math.max(-hx, Math.min(hx, localCenter.x));
    const cy = Math.max(-hy, Math.min(hy, localCenter.y));
    const cz = Math.max(-hz, Math.min(hz, localCenter.z));

    const dx = localCenter.x - cx;
    const dy = localCenter.y - cy;
    const dz = localCenter.z - cz;
    const distSq = dx * dx + dy * dy + dz * dz;

    if (distSq > radius * radius) {
      return null;
    }

    const localNormal = new Vec3();
    let penetration = 0;
    let localPoint = new Vec3(cx, cy, cz);

    if (distSq > 1e-10) {
      const dist = Math.sqrt(distSq);
      penetration = radius - dist;
      localNormal.set(dx / dist, dy / dist, dz / dist);
    } else {
      const distMinX = localCenter.x - (-hx);
      const distMaxX = hx - localCenter.x;
      const distMinY = localCenter.y - (-hy);
      const distMaxY = hy - localCenter.y;
      const distMinZ = localCenter.z - (-hz);
      const distMaxZ = hz - localCenter.z;

      let minDist = distMinX;
      localNormal.set(-1, 0, 0);
      localPoint.set(-hx, localCenter.y, localCenter.z);

      if (distMaxX < minDist) {
        minDist = distMaxX;
        localNormal.set(1, 0, 0);
        localPoint.set(hx, localCenter.y, localCenter.z);
      }
      if (distMinY < minDist) {
        minDist = distMinY;
        localNormal.set(0, -1, 0);
        localPoint.set(localCenter.x, -hy, localCenter.z);
      }
      if (distMaxY < minDist) {
        minDist = distMaxY;
        localNormal.set(0, 1, 0);
        localPoint.set(localCenter.x, hy, localCenter.z);
      }
      if (distMinZ < minDist) {
        minDist = distMinZ;
        localNormal.set(0, 0, -1);
        localPoint.set(localCenter.x, localCenter.y, -hz);
      }
      if (distMaxZ < minDist) {
        minDist = distMaxZ;
        localNormal.set(0, 0, 1);
        localPoint.set(localCenter.x, localCenter.y, hz);
      }

      penetration = radius + minDist;
    }

    const worldNormal = this.localToWorldVec(localNormal).normalize();
    const worldPoint = this.localToWorld(localPoint);

    return {
      hit: true,
      penetration,
      normal: worldNormal,
      point: worldPoint,
      collider: this
    };
  }

  testCapsule(baseCenter, topCenter, radius) {
    const localBase = this.worldToLocal(baseCenter);
    const localTop = this.worldToLocal(topCenter);

    const segDir = new Vec3().subVectors(localTop, localBase);
    const segLenSq = segDir.lengthSq();

    let bestT = 0;
    if (segLenSq > 1e-8) {
      if (Math.abs(segDir.x) < 1e-4 && Math.abs(segDir.z) < 1e-4) {
        const clampedY = Math.max(-this.halfExtents.y, Math.min(this.halfExtents.y, (localBase.y + localTop.y) * 0.5));
        bestT = Math.max(0, Math.min(1, (clampedY - localBase.y) / (localTop.y - localBase.y)));
      } else {
        const midPoint = new Vec3().addVectors(localBase, localTop).multiplyScalar(0.5);
        const clampedMid = new Vec3(
          Math.max(-this.halfExtents.x, Math.min(this.halfExtents.x, midPoint.x)),
          Math.max(-this.halfExtents.y, Math.min(this.halfExtents.y, midPoint.y)),
          Math.max(-this.halfExtents.z, Math.min(this.halfExtents.z, midPoint.z))
        );
        const toClamped = new Vec3().subVectors(clampedMid, localBase);
        bestT = Math.max(0, Math.min(1, toClamped.dot(segDir) / segLenSq));
      }
    }

    const testCenterLocal = new Vec3().addVectors(localBase, new Vec3().copy(segDir).multiplyScalar(bestT));
    const testCenterWorld = this.localToWorld(testCenterLocal);

    return this.testSphere(testCenterWorld, radius);
  }
}

/**
 * StairRampCollider: Smooth stair ramp collision representation for character climbing.
 */
export class StairRampCollider extends Collider {
  constructor(options = {}) {
    super({ ...options, type: 'stair_ramp', isWalkable: true });

    this.bottom = Vec3.from(options.bottom || options.startPoint || { x: 0, y: 0, z: 0 });
    this.top = Vec3.from(options.top || options.endPoint || { x: 0, y: 2.4, z: 4 });
    this.width = options.width || 2.0;
    this.thickness = options.thickness || 0.8;

    const delta = new Vec3().subVectors(this.top, this.bottom);
    this.height = delta.y;

    this.runVec = new Vec3(delta.x, 0, delta.z);
    this.runLength = this.runVec.length();
    if (this.runLength < 1e-4) {
      this.runForward = new Vec3(0, 0, 1);
      this.runLength = 1.0;
    } else {
      this.runForward = this.runVec.clone().normalize();
    }

    this.runRight = new Vec3(this.runForward.z, 0, -this.runForward.x).normalize();

    this.slopeVec = delta.clone();
    this.slopeLength = this.slopeVec.length();

    this.surfaceNormal = new Vec3().crossVectors(this.runRight, this.slopeVec).normalize();
    if (this.surfaceNormal.y < 0) {
      this.surfaceNormal.negate();
    }

    this.updateBounds();
  }

  updateBounds() {
    const halfW = this.width * 0.5;
    const wVec = new Vec3().copy(this.runRight).multiplyScalar(halfW);

    const c0 = new Vec3().addVectors(this.bottom, wVec);
    const c1 = new Vec3().subVectors(this.bottom, wVec);
    const c2 = new Vec3().addVectors(this.top, wVec);
    const c3 = new Vec3().subVectors(this.top, wVec);

    const corners = [c0, c1, c2, c3];

    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

    for (const c of corners) {
      minX = Math.min(minX, c.x);
      minY = Math.min(minY, c.y - this.thickness);
      minZ = Math.min(minZ, c.z);

      maxX = Math.max(maxX, c.x);
      maxY = Math.max(maxY, c.y + 0.1);
      maxZ = Math.max(maxZ, c.z);
    }

    this.bounds.min.set(minX, minY, minZ);
    this.bounds.max.set(maxX, maxY, maxZ);
  }

  getRunCoordinates(point) {
    const diff = new Vec3().subVectors(point, this.bottom);
    const u = diff.x * this.runForward.x + diff.z * this.runForward.z;
    const v = diff.x * this.runRight.x + diff.z * this.runRight.z;
    return { u, v };
  }

  getSurfaceHeightAt(x, z) {
    const diffX = x - this.bottom.x;
    const diffZ = z - this.bottom.z;
    const u = diffX * this.runForward.x + diffZ * this.runForward.z;
    const clampedU = Math.max(0, Math.min(this.runLength, u));
    const fraction = clampedU / this.runLength;
    return this.bottom.y + fraction * this.height;
  }

  raycast(rayOrigin, rayDir, maxDist = Infinity) {
    const denom = this.surfaceNormal.dot(rayDir);
    if (Math.abs(denom) < 1e-6) return null;

    const diff = new Vec3().subVectors(this.bottom, rayOrigin);
    const t = diff.dot(this.surfaceNormal) / denom;
    if (t < 0 || t > maxDist) return null;

    const hitPoint = new Vec3().addVectors(rayOrigin, new Vec3().copy(rayDir).multiplyScalar(t));
    const { u, v } = this.getRunCoordinates(hitPoint);

    const halfW = this.width * 0.5;
    if (u >= -0.05 && u <= this.runLength + 0.05 && v >= -halfW - 0.05 && v <= halfW + 0.05) {
      return {
        hit: true,
        distance: t,
        point: hitPoint,
        normal: this.surfaceNormal.clone(),
        collider: this
      };
    }
    return null;
  }

  testSphere(center, radius) {
    const { u, v } = this.getRunCoordinates(center);
    const halfW = this.width * 0.5;

    // Check lateral width bounds
    if (v < -halfW - radius || v > halfW + radius) {
      return null;
    }

    // Only interact when along the ramp run [0, runLength]
    if (u < -0.05 || u > this.runLength + 0.05) {
      return null;
    }

    const clampedU = Math.max(0, Math.min(this.runLength, u));
    const clampedV = Math.max(-halfW, Math.min(halfW, v));

    const surfPoint = new Vec3()
      .copy(this.bottom)
      .addScaledVector(this.runForward, clampedU)
      .addScaledVector(this.runRight, clampedV);
    surfPoint.y = this.bottom.y + (clampedU / this.runLength) * this.height;

    const toCenter = new Vec3().subVectors(center, surfPoint);
    const distAlongNormal = toCenter.dot(this.surfaceNormal);

    if (distAlongNormal <= radius && distAlongNormal > -this.thickness) {
      const penetration = radius - distAlongNormal;
      return {
        hit: true,
        penetration,
        normal: this.surfaceNormal.clone(),
        point: surfPoint,
        collider: this
      };
    }

    return null;
  }

  testCapsule(baseCenter, topCenter, radius) {
    return this.testSphere(baseCenter, radius);
  }
}

/**
 * SphereCollider for dynamic objects or round props.
 */
export class SphereCollider extends Collider {
  constructor(options = {}) {
    super({ ...options, type: 'sphere' });
    this.center = Vec3.from(options.center || options.position || { x: 0, y: 0, z: 0 });
    this.radius = options.radius || 0.5;
    this.updateBounds();
  }

  updateBounds() {
    this.bounds.min.set(this.center.x - this.radius, this.center.y - this.radius, this.center.z - this.radius);
    this.bounds.max.set(this.center.x + this.radius, this.center.y + this.radius, this.center.z + this.radius);
  }

  testSphere(center, radius) {
    const diff = new Vec3().subVectors(center, this.center);
    const distSq = diff.lengthSq();
    const totalR = this.radius + radius;

    if (distSq <= totalR * totalR) {
      const dist = Math.sqrt(distSq);
      const normal = dist > 1e-6 ? diff.multiplyScalar(1 / dist) : new Vec3(0, 1, 0);
      const penetration = totalR - dist;
      const point = new Vec3().copy(this.center).addScaledVector(normal, this.radius);
      return {
        hit: true,
        penetration,
        normal,
        point,
        collider: this
      };
    }
    return null;
  }

  testCapsule(baseCenter, topCenter, radius) {
    const segDir = new Vec3().subVectors(topCenter, baseCenter);
    const segLenSq = segDir.lengthSq();
    let t = 0;
    if (segLenSq > 1e-8) {
      const toSphere = new Vec3().subVectors(this.center, baseCenter);
      t = Math.max(0, Math.min(1, toSphere.dot(segDir) / segLenSq));
    }
    const closestPoint = new Vec3().copy(baseCenter).addScaledVector(segDir, t);
    return this.testSphere(closestPoint, radius);
  }
}
